import {
  parseApiCashPolicySplitSchedule,
  waterfallScheduleRequestFromPolicy,
  type WaterfallScheduleRequest,
  type WaterfallScheduleResponse,
  type YieldCurveResponse,
} from '@/services/projections'
import { scheduleIsValid } from '../engine/cashPolicy'
import type { CashPolicy, EvaluateContext } from '../engine/types'

export type HydrateCashPolicyDeps = {
  computeWaterfallSchedule: (body: WaterfallScheduleRequest) => Promise<WaterfallScheduleResponse>
  getYieldCurve: () => Promise<YieldCurveResponse>
}

export async function hydrateCashPolicy(
  policy: CashPolicy | null,
  context: EvaluateContext,
  deps: HydrateCashPolicyDeps,
  options: { forceWaterfall?: boolean } = {},
): Promise<{ policy: CashPolicy | null; problems: string[] }> {
  const problems: string[] = []
  if (!policy) {
    return { policy: null, problems }
  }

  let hydrated = structuredClone(policy)

  const hasAmount = hydrated.distribution.amount_per_period != null
  const needsWaterfall =
    hydrated.enabled &&
    hasAmount &&
    (Boolean(options.forceWaterfall) ||
      !scheduleIsValid(hydrated.distribution.split_schedule, hydrated, 36, context) ||
      !Array.isArray(hydrated.distribution.split_schedule?.pref_accrual))

  if (needsWaterfall) {
    try {
      const response = await deps.computeWaterfallSchedule(
        waterfallScheduleRequestFromPolicy(hydrated),
      )
      hydrated = {
        ...hydrated,
        distribution: {
          ...hydrated.distribution,
          split_schedule: parseApiCashPolicySplitSchedule(response.split_schedule),
        },
      }
    } catch {
      problems.push('Waterfall schedule failed')
      hydrated = {
        ...hydrated,
        distribution: { ...hydrated.distribution, split_schedule: null },
      }
    }
  }

  if (hydrated.sweep.enabled && hydrated.sweep.yield_snapshot == null) {
    try {
      const curve = await deps.getYieldCurve()
      hydrated = {
        ...hydrated,
        sweep: {
          ...hydrated.sweep,
          yield_snapshot: {
            as_of: curve.as_of,
            source: curve.source,
            spread: Number(curve.spread),
            apr_by_month: curve.apr_by_month.map((apr) => Number(apr)),
          },
        },
      }
    } catch {
      problems.push('Could not load the Treasury yield curve for cash policy sweep.')
    }
  }

  return { policy: hydrated, problems }
}
